import type { LayerSpecification, Map as MlMap } from "maplibre-gl";
import type { OsmBackgroundOptions, OsmStyleName, TileLayerName } from "../types";

/**
 * Les quatre déclarent les mêmes `sources`, `glyphs` et `sprite` : c'est cet invariant qui permet de
 * changer de style en remplaçant le seul tableau de calques, sans `map.setStyle()` ni
 * `setSprite`/`setGlyphs`. Un style qui en dévierait s'afficherait sans ses icônes.
 */
export const OSM_STYLE_URLS: Record<OsmStyleName, string> = {
  light: "https://tiles.openfreemap.org/styles/positron",
  dark: "https://tiles.openfreemap.org/styles/dark",
  sober: "https://tiles.openfreemap.org/styles/fiord",
  detailed: "https://tiles.openfreemap.org/styles/liberty",
};

// Chargé à la construction de chaque instance MapLibre : il apporte les sources/polices/sprite
// communs à tous les autres, d'où le fait que `"light"` ne coûte aucune requête supplémentaire.
export const DEFAULT_STYLE = OSM_STYLE_URLS.light;

/**
 * Fond de carte satellite (voir `TileLayerName`) : tuiles raster Esri World Imagery, gratuites sans
 * clé d'API (contrairement à la plupart des fournisseurs d'imagerie satellite) — pas de style
 * vectoriel équivalent chez OpenFreeMap (qui ne fournit que des styles "plan", pas d'imagerie aérienne).
 */
export const SATELLITE_TILE_SOURCE_ID = "background-satellite";
export const SATELLITE_TILE_LAYER_ID = "background-satellite";
export const SATELLITE_TILE_URL =
  "https://server.arcgisonline.com/ArcGIS/rest/services/World_Imagery/MapServer/tile/{z}/{y}/{x}";
export const SATELLITE_TILE_ATTRIBUTION = "Esri, Maxar, Earthstar Geographics";

const DEFAULT_OSM_OPTIONS: Required<OsmBackgroundOptions> = {
  style: "light",
  language: "fr",
  placeLabels: true,
  roadLabels: true,
  poiLabels: true,
  buildings3d: false,
};

export type ResolvedOsmOptions = typeof DEFAULT_OSM_OPTIONS;

/** `language` se teste contre `undefined` et non par `??` : `null` y est une valeur significative
 * (garder le nom local), à ne pas confondre avec "champ non renseigné". */
export function resolveOsmOptions(options: OsmBackgroundOptions | undefined): ResolvedOsmOptions {
  return {
    style: options?.style ?? DEFAULT_OSM_OPTIONS.style,
    language: options?.language !== undefined ? options.language : DEFAULT_OSM_OPTIONS.language,
    placeLabels: options?.placeLabels ?? DEFAULT_OSM_OPTIONS.placeLabels,
    roadLabels: options?.roadLabels ?? DEFAULT_OSM_OPTIONS.roadLabels,
    poiLabels: options?.poiLabels ?? DEFAULT_OSM_OPTIONS.poiLabels,
    buildings3d: options?.buildings3d ?? DEFAULT_OSM_OPTIONS.buildings3d,
  };
}

/** La PROMESSE est mémorisée, pas son résultat : deux appels rapprochés partagent un seul `fetch`.
 * Une promesse rompue est retirée, laissant un appel ultérieur réessayer. */
const osmStyleLayersCache = new Map<OsmStyleName, Promise<LayerSpecification[]>>();

/** Systématique à la sortie du cache, partagé par toutes les cartes : un `LayerSpecification` remis
 * à `map.addLayer()` appartient ensuite à cette carte-là. */
function cloneLayers(layers: LayerSpecification[]): LayerSpecification[] {
  return JSON.parse(JSON.stringify(layers)) as LayerSpecification[];
}

export function primeOsmStyleLayers(style: OsmStyleName, layers: LayerSpecification[]): void {
  if (!osmStyleLayersCache.has(style)) osmStyleLayersCache.set(style, Promise.resolve(cloneLayers(layers)));
}

/** En copie propre à l'appelant. Rejette si le style est injoignable. */
export function fetchOsmStyleLayers(style: OsmStyleName): Promise<LayerSpecification[]> {
  const cached = osmStyleLayersCache.get(style);
  if (cached) return cached.then(cloneLayers);

  const pending = fetch(OSM_STYLE_URLS[style])
    .then((response) => {
      if (!response.ok) throw new Error(`Style OSM "${style}" injoignable (HTTP ${response.status})`);
      return response.json() as Promise<{ layers?: LayerSpecification[] }>;
    })
    .then((json) => json.layers ?? [])
    .catch((error) => {
      osmStyleLayersCache.delete(style);
      throw error;
    });

  osmStyleLayersCache.set(style, pending);
  return pending.then(cloneLayers);
}

/**
 * Classement par `source-layer` et jamais par id de calque : les ids diffèrent d'un style à l'autre
 * (`label_city` ici, `place_city` là) alors que le schéma OpenMapTiles leur est commun.
 *
 * `transportation` est rangé avec les noms de rues bien qu'il ne porte pas de texte : ce sont les
 * flèches de sens unique, qui n'ont pas à survivre seules au masquage des noms de voies.
 */
const SYMBOL_SOURCE_LAYERS: Record<"place" | "road" | "poi", readonly string[]> = {
  place: ["place", "water_name", "waterway", "aerodrome_label"],
  road: ["transportation_name", "transportation"],
  poi: ["poi"],
};

/** `undefined` pour un calque non-`symbol` comme pour un `source-layer` inconnu, qui reste donc
 * toujours affiché : on ne masque jamais ce qu'on n'a pas su reconnaître. */
export function symbolLayerCategory(layer: LayerSpecification): "place" | "road" | "poi" | undefined {
  if (layer.type !== "symbol") return undefined;
  const sourceLayer = (layer as { "source-layer"?: string })["source-layer"];
  if (!sourceLayer) return undefined;
  for (const [category, sourceLayers] of Object.entries(SYMBOL_SOURCE_LAYERS)) {
    if (sourceLayers.includes(sourceLayer)) return category as "place" | "road" | "poi";
  }
  return undefined;
}

/** Vrai si l'expression lit un champ de nom — par opposition aux écussons routiers, qui lisent `ref`. */
function readsNameField(expression: unknown): boolean {
  if (!Array.isArray(expression)) return false;
  if (expression[0] === "get" && typeof expression[1] === "string" && expression[1].startsWith("name")) return true;
  return expression.some(readsNameField);
}

/**
 * Passe les libellés dans `language` (code OSM, voir `OsmBackgroundOptions.language`), en préfixant
 * leur `text-field` d'un nom localisé plutôt qu'en le remplaçant : l'expression d'origine reste le
 * repli, ce qui préserve son traitement des écritures non latines (concaténation latin + non latin)
 * là où OSM n'a pas de nom dans cette langue.
 *
 * Les calques sans champ de nom sont laissés tels quels : un écusson routier affiche `ref`, et lui
 * ajouter un repli localisé y ferait apparaître le nom de la rue à la place du numéro de route.
 *
 * `language` à `null`/`undefined` : aucune traduction imposée, les calques ressortent intacts.
 */
export function localizeStyleLayers(
  layers: LayerSpecification[],
  language: string | null | undefined,
): LayerSpecification[] {
  if (!language) return layers;

  return layers.map((layer) => {
    if (layer.type !== "symbol") return layer;

    const textField = layer.layout?.["text-field"];
    if (!readsNameField(textField)) return layer;

    return {
      ...layer,
      layout: {
        ...layer.layout,
        "text-field": ["coalesce", ["get", `name:${language}`], textField],
      },
    } as LayerSpecification;
  });
}

export const OSM_BUILDING_3D_LAYER_ID = "background-osm-building-3d";

/** Le `source-layer` `building` n'est servi qu'à partir de 13/14 selon le style. */
const BUILDING_3D_MIN_ZOOM = 14;

const BUILDING_3D_FALLBACK_COLOR = "hsl(35,8%,85%)";

/**
 * Construit ici plutôt qu'emprunté au style, que tous ne fournissent pas. Sa couleur est lue sur le
 * calque `building` du style en cours, pour s'y accorder sans table de correspondance.
 */
export function buildingExtrusionLayer(layers: LayerSpecification[]): LayerSpecification | null {
  const buildingLayer = layers.find(
    (layer): layer is Extract<LayerSpecification, { type: "fill" }> =>
      layer.type === "fill" && layer["source-layer"] === "building",
  );
  if (!buildingLayer) return null;

  const extrusion: Extract<LayerSpecification, { type: "fill-extrusion" }> = {
    id: OSM_BUILDING_3D_LAYER_ID,
    type: "fill-extrusion",
    source: buildingLayer.source,
    "source-layer": "building",
    minzoom: BUILDING_3D_MIN_ZOOM,
    paint: {
      "fill-extrusion-color": buildingLayer.paint?.["fill-color"] ?? BUILDING_3D_FALLBACK_COLOR,
      "fill-extrusion-height": ["get", "render_height"],
      "fill-extrusion-base": ["get", "render_min_height"],
      // Légère transparence : une façade opaque écrase les formes dessinées qu'elle recouvre en vue
      // inclinée, alors que le fond doit rester un décor. Même valeur que le `building-3d` de liberty.
      "fill-extrusion-opacity": 0.8,
    },
  };

  return extrusion;
}

/**
 * Rend le tableau prêt pour `addBackgroundTileLayer`. Masquage par retrait plutôt que par
 * `visibility: "none"` : un calque jamais ajouté ne coûte rien au moteur.
 */
export function applyOsmOptions(rawLayers: LayerSpecification[], options: OsmBackgroundOptions | undefined): LayerSpecification[] {
  const resolved = resolveOsmOptions(options);
  const visible: Record<"place" | "road" | "poi", boolean> = {
    place: resolved.placeLabels,
    road: resolved.roadLabels,
    poi: resolved.poiLabels,
  };

  const kept = rawLayers.filter((layer) => {
    const category = symbolLayerCategory(layer);
    return category === undefined || visible[category];
  });

  const layers = localizeStyleLayers(kept, resolved.language);

  if (!resolved.buildings3d) return layers;

  const extrusion = buildingExtrusionLayer(rawLayers);
  return extrusion ? [...layers, extrusion] : layers;
}

/**
 * Capture les calques du style vectoriel `DEFAULT_STYLE` juste chargé sur `map`, puis les retire —
 * factorisé entre `MapLibreAdapter.init()` (carte principale) et `BackgroundMap` (carte de fond
 * secondaire) : ce cycle "charger DEFAULT_STYLE → capturer ses layers → les retirer" doit être rejoué
 * à l'identique sur CHAQUE instance, MapLibre ne peignant que ce que contient le style de LA carte
 * où on l'ajoute.
 *
 * Rend les calques BRUTS : c'est `applyOsmOptions` qui en dérive ce qui sera peint. Ils alimentent
 * au passage le cache de styles, le style clair étant déjà téléchargé à ce stade.
 */
export function captureAndStripStyleLayers(map: MlMap): LayerSpecification[] {
  const layers = map.getStyle().layers ?? [];
  for (const layer of layers) {
    map.removeLayer(layer.id);
  }
  primeOsmStyleLayers("light", layers);
  return layers;
}

/** Retire le fond `name` de `map` — `styleLayers` = le tableau EXACTEMENT tel qu'il a été passé à
 * `addBackgroundTileLayer` (voir `MapLibreAdapter.paintedOsmLayers` : le tableau peint dépend des
 * `OsmBackgroundOptions`, en repasser un autre laisserait des calques orphelins). No-op pour
 * `"unset"` (rien n'est jamais affiché). */
export function removeBackgroundTileLayer(map: MlMap, name: TileLayerName, styleLayers: LayerSpecification[]): void {
  switch (name) {
    case "osm":
      for (const layer of styleLayers) {
        if (map.getLayer(layer.id)) map.removeLayer(layer.id);
      }
      break;
    case "satellite":
      if (map.getLayer(SATELLITE_TILE_LAYER_ID)) map.removeLayer(SATELLITE_TILE_LAYER_ID);
      if (map.getSource(SATELLITE_TILE_SOURCE_ID)) map.removeSource(SATELLITE_TILE_SOURCE_ID);
      break;
    case "unset":
      break;
  }
}

/**
 * Affiche le fond `name` sur `map` — `"osm"` ajoute les calques de `styleLayers` (ceux qu'a produits
 * `applyOsmOptions`), `"satellite"` ajoute une source/un calque raster (Esri World Imagery, voir
 * `SATELLITE_TILE_URL`). `beforeId`, si fourni, insère le fond sous ce calque plutôt qu'au sommet —
 * utile quand `map` porte déjà d'autres calques à garder au-dessus (voir
 * `MapLibreAdapter.getBottomLayerId`) ; sans objet sur une carte n'ayant vocation à ne jamais
 * recevoir d'autre calque (`BackgroundMap`). No-op pour `"unset"`.
 */
export function addBackgroundTileLayer(
  map: MlMap,
  name: TileLayerName,
  styleLayers: LayerSpecification[],
  beforeId?: string,
): void {
  switch (name) {
    case "osm":
      for (const layer of styleLayers) {
        map.addLayer(layer, beforeId);
      }
      break;
    case "satellite":
      map.addSource(SATELLITE_TILE_SOURCE_ID, {
        type: "raster",
        tiles: [SATELLITE_TILE_URL],
        tileSize: 256,
        attribution: SATELLITE_TILE_ATTRIBUTION,
      });
      map.addLayer({ id: SATELLITE_TILE_LAYER_ID, type: "raster", source: SATELLITE_TILE_SOURCE_ID }, beforeId);
      break;
    case "unset":
      break;
  }
}
